/**
 * Role Management Service
 * Handles all role-related API calls to the FastAPI backend.
 */

import { apiRequest } from '@/lib/api-client'
import { logError, logInfo } from '@/lib/logger'
import { getCurrentUserId } from '@/services/auth-service'
import { myAppsService } from '@/services/my-apps-service'
import type { SupervisorPermissions } from '@/services/client-access-service'
import type {
  CdbTqsKnowledgeAreaBase,
  CdbTqsKnowledgeAreaResponse,
  CreateKnowledgeAreaPayload,
} from '@/types/cdb-tqs-knowledge-areas'

// ============== Types ==============

export interface AllowedTWLevels {
  tw4: boolean
  tw3: boolean
  tw2: boolean
  tw1: boolean
  websearch: boolean
}

// Per-role CDB Training Hub module permissions. 'umfrage' can publish public,
// unauthenticated response links, so it stays opt-in. Aggregated OR across all
// of a user's roles in the backend (see backend/app/api/routes/roles.py).
export interface AllowedTrainingModules {
  schulung: boolean
  umfrage: boolean
}

export const DEFAULT_ALLOWED_TRAINING_MODULES: AllowedTrainingModules = {
  schulung: true,
  umfrage: false,
}

// Ticket types a role may create (#1544, migration 080). Both on unless an
// admin switches one off. Aggregated OR across a user's roles; a user without
// any role keeps both (backend/app/services/ticket_permissions.py).
export interface AllowedTicketTypes {
  bug: boolean
  feature: boolean
}

export const DEFAULT_ALLOWED_TICKET_TYPES: AllowedTicketTypes = {
  bug: true,
  feature: true,
}

// Knowledge areas granted for one app (cdb_tqs#936). The same area can be
// granted to the Communicator and withheld from the Knowledge Extractor —
// which is what read-only access to a knowledge area is made of.
export interface AppKnowledgeAreaGrant {
  app_id: string
  knowledge_area_ids: string[]
  /** The same areas with "read" (false) or "verify" (true); matters for the
   *  Knowledge Verifier only (#1674). Always in responses; in a request an
   *  area left out keeps its stored value, and a new one verifies. */
  knowledge_areas?: { knowledge_area_id: string; can_verify: boolean }[]
}

export interface Role {
  id: string
  name: string
  description: string | null
  is_system: boolean
  client_id: string | null
  client_name?: string | null
  role_type: 'user' | 'supervisor'
  allowed_tw_levels: AllowedTWLevels
  allowed_training_modules: AllowedTrainingModules
  allowed_ticket_types?: AllowedTicketTypes
  app_count?: number
  knowledge_area_count?: number
  user_count?: number
  created_at: string
  updated_at: string
  created_by?: string | null
}

export interface CreateRoleData {
  name: string
  description?: string
  client_id?: string | null
  role_type?: 'user' | 'supervisor'
  allowed_tw_levels: AllowedTWLevels
  allowed_training_modules?: AllowedTrainingModules
  allowed_ticket_types?: AllowedTicketTypes
  app_ids: string[]
  knowledge_area_ids: string[]
  // Omit to grant knowledge_area_ids to every KA-aware app of the role
  // (pre-#936 behaviour); send it to grant per app.
  app_knowledge_areas?: AppKnowledgeAreaGrant[]
}

interface UpdateRoleData {
  name?: string
  description?: string
  client_id?: string | null
  role_type?: 'user' | 'supervisor'
  allowed_tw_levels?: AllowedTWLevels
  allowed_training_modules?: AllowedTrainingModules
  allowed_ticket_types?: AllowedTicketTypes
  app_ids?: string[]
  knowledge_area_ids?: string[]
  app_knowledge_areas?: AppKnowledgeAreaGrant[]
}

export interface SupervisorStatus {
  is_supervisor: boolean
  is_admin: boolean
  client_id: string | null
  client_name: string | null
  /** The organisation's products: what the caller may switch per user of it (#1586). */
  grantable_applications?: string[]
  /** The organisation's switches (#1587); admins are not bound by them. */
  supervisor_permissions?: SupervisorPermissions
}

export interface SupervisorUser {
  id: string
  email: string
  role: string
  is_active: boolean
  client_id: string | null
  created_at: string | null
}

interface SupervisorUsersResponse {
  users: SupervisorUser[]
  total: number
  client_id: string
}

export interface UserPermissions {
  user_id: string
  app_ids: string[]
  knowledge_area_ids: string[]
  /** `/supervisor/me/permissions` only: areas the supervisor may grant as "verify" (#1674). */
  verify_knowledge_area_ids?: string[]
  allowed_tw_levels: AllowedTWLevels
  allowed_training_modules: AllowedTrainingModules
  allowed_ticket_types?: AllowedTicketTypes
}

// Role templates (tqs#742): client-agnostic role presets, admin-only.
// Kept separate from roles on purpose — they never appear in role listings,
// supervisor delegation or permission aggregation.
export interface RoleTemplate {
  id: string
  name: string
  description: string | null
  allowed_tw_levels: AllowedTWLevels
  allowed_training_modules: AllowedTrainingModules
  allowed_ticket_types?: AllowedTicketTypes
  app_ids: string[]
  created_at: string
  updated_at: string
  created_by?: string | null
}

export interface CreateRoleTemplateData {
  name: string
  description?: string
  allowed_tw_levels: AllowedTWLevels
  allowed_training_modules: AllowedTrainingModules
  allowed_ticket_types?: AllowedTicketTypes
  app_ids: string[]
}

interface RoleTemplateListResponse {
  templates: RoleTemplate[]
  total: number
}

interface RoleListResponse {
  roles: Role[]
  total: number
}

interface RoleAppAssignmentsResponse {
  role_id: string
  app_ids: string[]
}

interface RoleKnowledgeAreaAssignmentsResponse {
  role_id: string
  knowledge_area_ids: string[]
  app_knowledge_areas: AppKnowledgeAreaGrant[]
}

interface UserRolesResponse {
  user_id: string
  roles: Role[]
}

interface UsersWithRoleResponse {
  role_id: string
  user_ids: string[]
}

// Role capability restrictions (cdb_tqs#790): per-role take-away deltas on top
// of the client tier. A key in ``capability_keys`` means the role restricts it.
interface RoleCapabilityRestrictionsResponse {
  role_id: string
  capability_keys: string[]
}

// ============== Role Management Service ==============

export const roleManagementService = {
  // ============== Roles CRUD ==============

  async getRoles(): Promise<Role[]> {
    try {
      const response = await apiRequest<RoleListResponse>('/roles')
      logInfo({ context: 'RoleManagementService' }, `Loaded ${response?.roles.length ?? 0} roles`)
      return response?.roles ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getRoles' })
      return []
    }
  },

  async getRoleById(roleId: string): Promise<Role | null> {
    try {
      return await apiRequest<Role>(`/roles/${roleId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getRoleById', roleId })
      return null
    }
  },

  async createRole(roleData: CreateRoleData): Promise<Role> {
    let created: Role | null
    try {
      created = await apiRequest<Role>('/roles', {
        method: 'POST',
        body: JSON.stringify(roleData),
      })
      if (created) {
        logInfo({ context: 'RoleManagementService' }, `Created role: ${created.name}`)
      }
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'createRole' })
      throw error
    }
    if (!created) {
      throw new Error('Fehler beim Erstellen der Rolle')
    }
    return created
  },

  async updateRole(roleId: string, updates: Partial<CreateRoleData>): Promise<void> {
    const updateData: UpdateRoleData = {
      name: updates.name,
      description: updates.description,
      client_id: updates.client_id,
      allowed_tw_levels: updates.allowed_tw_levels,
      allowed_training_modules: updates.allowed_training_modules,
      allowed_ticket_types: updates.allowed_ticket_types,
      app_ids: updates.app_ids,
      knowledge_area_ids: updates.knowledge_area_ids,
      app_knowledge_areas: updates.app_knowledge_areas,
    }
    try {
      const updated = await apiRequest<Role>(`/roles/${roleId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      })
      if (updated) {
        logInfo({ context: 'RoleManagementService' }, `Updated role: ${updated.name}`)
      }
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'updateRole', roleId })
      throw error
    }
  },

  async deleteRole(roleId: string): Promise<void> {
    try {
      await apiRequest(`/roles/${roleId}`, { method: 'DELETE' })
      logInfo({ context: 'RoleManagementService' }, `Deleted role: ${roleId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'deleteRole', roleId })
      throw error
    }
  },

  // ============== Role Templates (admin only, tqs#742) ==============

  async getRoleTemplates(): Promise<RoleTemplate[]> {
    try {
      const response = await apiRequest<RoleTemplateListResponse>('/role-templates')
      logInfo({ context: 'RoleManagementService' }, `Loaded ${response?.templates.length ?? 0} role templates`)
      return response?.templates ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getRoleTemplates' })
      return []
    }
  },

  async createRoleTemplate(templateData: CreateRoleTemplateData): Promise<RoleTemplate> {
    let created: RoleTemplate | null
    try {
      created = await apiRequest<RoleTemplate>('/role-templates', {
        method: 'POST',
        body: JSON.stringify(templateData),
      })
      if (created) {
        logInfo({ context: 'RoleManagementService' }, `Created role template: ${created.name}`)
      }
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'createRoleTemplate' })
      throw error
    }
    if (!created) {
      throw new Error('Fehler beim Erstellen der Vorlage')
    }
    return created
  },

  async updateRoleTemplate(templateId: string, updates: Partial<CreateRoleTemplateData>): Promise<void> {
    try {
      await apiRequest<RoleTemplate>(`/role-templates/${templateId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })
      logInfo({ context: 'RoleManagementService' }, `Updated role template: ${templateId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'updateRoleTemplate', templateId })
      throw error
    }
  },

  async deleteRoleTemplate(templateId: string): Promise<void> {
    try {
      await apiRequest(`/role-templates/${templateId}`, { method: 'DELETE' })
      logInfo({ context: 'RoleManagementService' }, `Deleted role template: ${templateId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'deleteRoleTemplate', templateId })
      throw error
    }
  },

  // ============== Role App Assignments ==============

  async getRoleAppAssignments(roleId: string): Promise<string[]> {
    try {
      const response = await apiRequest<RoleAppAssignmentsResponse>(`/roles/${roleId}/apps`)
      return response?.app_ids ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getRoleAppAssignments', roleId })
      return []
    }
  },

  async assignAppToRole(roleId: string, appId: string): Promise<void> {
    try {
      await apiRequest(`/roles/${roleId}/apps`, {
        method: 'POST',
        body: JSON.stringify({ app_id: appId }),
      })
      logInfo({ context: 'RoleManagementService' }, `Assigned app ${appId} to role ${roleId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'assignAppToRole' })
      throw error
    }
  },

  async removeAppFromRole(roleId: string, appId: string): Promise<void> {
    try {
      await apiRequest(`/roles/${roleId}/apps/${appId}`, { method: 'DELETE' })
      logInfo({ context: 'RoleManagementService' }, `Removed app ${appId} from role ${roleId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'removeAppFromRole' })
      throw error
    }
  },

  // ============== Role Knowledge Area Assignments ==============

  async getRoleKnowledgeAreaAssignments(roleId: string): Promise<string[]> {
    try {
      const response = await apiRequest<RoleKnowledgeAreaAssignmentsResponse>(`/roles/${roleId}/knowledge-areas`)
      return response?.knowledge_area_ids ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getRoleKnowledgeAreaAssignments', roleId })
      return []
    }
  },

  /** Per-app knowledge area grants of a role — the wizard's edit source (#936). */
  async getRoleAppKnowledgeAreas(roleId: string, supervisorMode = false): Promise<AppKnowledgeAreaGrant[]> {
    const path = supervisorMode ? `/supervisor/roles/${roleId}/knowledge-areas` : `/roles/${roleId}/knowledge-areas`
    try {
      const response = await apiRequest<RoleKnowledgeAreaAssignmentsResponse>(path)
      return response?.app_knowledge_areas ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getRoleAppKnowledgeAreas', roleId })
      return []
    }
  },

  async assignKnowledgeAreaToRole(roleId: string, knowledgeAreaId: string): Promise<void> {
    try {
      await apiRequest(`/roles/${roleId}/knowledge-areas`, {
        method: 'POST',
        body: JSON.stringify({ knowledge_area_id: knowledgeAreaId }),
      })
      logInfo({ context: 'RoleManagementService' }, `Assigned knowledge area ${knowledgeAreaId} to role ${roleId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'assignKnowledgeAreaToRole' })
      throw error
    }
  },

  async removeKnowledgeAreaFromRole(roleId: string, knowledgeAreaId: string): Promise<void> {
    try {
      await apiRequest(`/roles/${roleId}/knowledge-areas/${knowledgeAreaId}`, { method: 'DELETE' })
      logInfo({ context: 'RoleManagementService' }, `Removed knowledge area ${knowledgeAreaId} from role ${roleId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'removeKnowledgeAreaFromRole' })
      throw error
    }
  },

  // ============== Role Capability Restrictions (cdb_tqs#790) ==============

  async getRoleCapabilityRestrictions(roleId: string): Promise<string[]> {
    try {
      const response = await apiRequest<RoleCapabilityRestrictionsResponse>(`/roles/${roleId}/capability-restrictions`)
      return response?.capability_keys ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getRoleCapabilityRestrictions', roleId })
      return []
    }
  },

  async setRoleCapabilityRestrictions(roleId: string, capabilityKeys: string[]): Promise<void> {
    try {
      await apiRequest(`/roles/${roleId}/capability-restrictions`, {
        method: 'PUT',
        body: JSON.stringify({ capability_keys: capabilityKeys }),
      })
      logInfo({ context: 'RoleManagementService' }, `Set ${capabilityKeys.length} capability restrictions on role ${roleId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'setRoleCapabilityRestrictions', roleId })
      throw error
    }
  },

  async getSupervisorRoleCapabilityRestrictions(roleId: string): Promise<string[]> {
    try {
      const response = await apiRequest<RoleCapabilityRestrictionsResponse>(`/supervisor/roles/${roleId}/capability-restrictions`)
      return response?.capability_keys ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getSupervisorRoleCapabilityRestrictions', roleId })
      return []
    }
  },

  async setSupervisorRoleCapabilityRestrictions(roleId: string, capabilityKeys: string[]): Promise<void> {
    try {
      await apiRequest(`/supervisor/roles/${roleId}/capability-restrictions`, {
        method: 'PUT',
        body: JSON.stringify({ capability_keys: capabilityKeys }),
      })
      logInfo({ context: 'RoleManagementService' }, `Set ${capabilityKeys.length} capability restrictions on supervisor role ${roleId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'setSupervisorRoleCapabilityRestrictions', roleId })
      throw error
    }
  },

  // ============== User Role Assignments ==============

  async getUserRoles(userId: string): Promise<Role[]> {
    try {
      const response = await apiRequest<UserRolesResponse>(`/roles/users/${userId}/roles`)
      return response?.roles ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getUserRoles', userId })
      return []
    }
  },

  async assignRoleToUser(userId: string, roleId: string): Promise<void> {
    try {
      await apiRequest(`/roles/users/${userId}/roles`, {
        method: 'POST',
        body: JSON.stringify({ role_id: roleId }),
      })
      logInfo({ context: 'RoleManagementService' }, `Assigned role ${roleId} to user ${userId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'assignRoleToUser' })
      throw error
    }
  },

  async removeRoleFromUser(userId: string, roleId: string): Promise<void> {
    try {
      await apiRequest(`/roles/users/${userId}/roles/${roleId}`, { method: 'DELETE' })
      logInfo({ context: 'RoleManagementService' }, `Removed role ${roleId} from user ${userId}`)
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'removeRoleFromUser' })
      throw error
    }
  },

  /**
   * Throws on failure, unlike `getUsersWithRole` / `getSupervisorUsersWithRole`:
   * an empty list there reads as "nobody holds the role", which is how a 500
   * once showed every user as unassigned in a dialog that writes (#1232).
   */
  async getRoleHolderIds(roleId: string, scope: 'admin' | 'supervisor'): Promise<string[]> {
    const path = scope === 'supervisor' ? `/supervisor/roles/${roleId}/users` : `/roles/${roleId}/users`
    const response = await apiRequest<UsersWithRoleResponse>(path)
    if (!response) {
      throw new Error('Empty role holders response')
    }
    return response.user_ids
  },

  async getUsersWithRole(roleId: string): Promise<string[]> {
    try {
      return await roleManagementService.getRoleHolderIds(roleId, 'admin')
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getUsersWithRole', roleId })
      return []
    }
  },

  // ============== User Permissions ==============

  /**
   * Get current user's own permissions (no admin required)
   */
  /**
   * Throws on failure — deliberately. It used to swallow every error and hand
   * back an all-denied object, which callers cannot tell apart from a user who
   * genuinely has no permissions: a network blip would drop a survey-only user
   * into the Training Hub's Schulung module, where every call then 403s. Callers
   * that want to degrade must say so themselves.
   */
  async getMyPermissions(): Promise<UserPermissions> {
    try {
      const response = await apiRequest<UserPermissions>(`/roles/me/permissions`)
      if (!response) {
        throw new Error('Empty permissions response')
      }
      return response
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getMyPermissions' })
      throw error
    }
  },

  /**
   * Get another user's permissions (admin only)
   */
  async getUserPermissions(userId: string): Promise<UserPermissions> {
    try {
      const response = await apiRequest<UserPermissions>(`/roles/users/${userId}/permissions`)
      return response ?? {
        user_id: userId,
        app_ids: [],
        knowledge_area_ids: [],
        allowed_tw_levels: { tw4: false, tw3: false, tw2: false, tw1: false, websearch: false },
        allowed_training_modules: DEFAULT_ALLOWED_TRAINING_MODULES,
      }
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getUserPermissions', userId })
      return {
        user_id: userId,
        app_ids: [],
        knowledge_area_ids: [],
        allowed_tw_levels: { tw4: false, tw3: false, tw2: false, tw1: false, websearch: false },
        allowed_training_modules: DEFAULT_ALLOWED_TRAINING_MODULES,
      }
    }
  },

  async canUserAccessAppViaRoles(userId: string, appId: string): Promise<boolean> {
    try {
      // Check if this is for the current user (use /me endpoint) or another user (admin only)
      const currentUserId = getCurrentUserId()
      const isOwnUser = currentUserId === userId

      if (isOwnUser) {
        // Own user: the cached /apps/me list, same rule as the BFF (#1610).
        return await myAppsService.hasApp(appId)
      } else {
        // Other users: admin-only endpoint, by app name.
        const response = await apiRequest<{ has_access: boolean }>(`/roles/users/${userId}/can-access-app/${appId}`)
        return response?.has_access ?? false
      }
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'canUserAccessAppViaRoles' })
      return false
    }
  },

  // ============== Supervisor ==============

  async getSupervisorStatus(): Promise<SupervisorStatus> {
    try {
      const response = await apiRequest<SupervisorStatus>('/supervisor/me/status')
      return response ?? { is_supervisor: false, is_admin: false, client_id: null, client_name: null }
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getSupervisorStatus' })
      return { is_supervisor: false, is_admin: false, client_id: null, client_name: null }
    }
  },

  /** Give a user of the supervisor's client one of the organisation's products, or take it back (#1586). */
  async setSupervisorApplication(userId: string, application: string, enabled: boolean): Promise<string[]> {
    const response = await apiRequest<{ user_id: string; allowed_applications: string[] }>(
      `/supervisor/users/${userId}/applications/${encodeURIComponent(application)}`,
      { method: 'PUT', body: JSON.stringify({ enabled }) },
    )
    return response?.allowed_applications ?? []
  },

  async getSupervisorPermissions(): Promise<UserPermissions> {
    try {
      const response = await apiRequest<UserPermissions>('/supervisor/me/permissions')
      return response ?? {
        user_id: '',
        app_ids: [],
        knowledge_area_ids: [],
        allowed_tw_levels: { tw4: false, tw3: false, tw2: false, tw1: false, websearch: false },
        allowed_training_modules: DEFAULT_ALLOWED_TRAINING_MODULES,
      }
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getSupervisorPermissions' })
      return {
        user_id: '',
        app_ids: [],
        knowledge_area_ids: [],
        allowed_tw_levels: { tw4: false, tw3: false, tw2: false, tw1: false, websearch: false },
        allowed_training_modules: DEFAULT_ALLOWED_TRAINING_MODULES,
      }
    }
  },

  async getSupervisorUsers(): Promise<SupervisorUser[]> {
    try {
      const response = await apiRequest<SupervisorUsersResponse>('/supervisor/users')
      return response?.users ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getSupervisorUsers' })
      return []
    }
  },

  async getSupervisorRoles(): Promise<Role[]> {
    try {
      const response = await apiRequest<{ roles: Role[]; total: number }>('/supervisor/roles')
      return response?.roles ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getSupervisorRoles' })
      return []
    }
  },

  async createSupervisorRole(roleData: CreateRoleData): Promise<Role> {
    let created: Role | null
    try {
      created = await apiRequest<Role>('/supervisor/roles', {
        method: 'POST',
        body: JSON.stringify(roleData),
      })
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'createSupervisorRole' })
      throw error
    }
    if (!created) throw new Error('Fehler beim Erstellen der Rolle')
    return created
  },

  async updateSupervisorRole(roleId: string, updates: Partial<CreateRoleData>): Promise<void> {
    const updateData: UpdateRoleData = {
      name: updates.name,
      description: updates.description,
      role_type: updates.role_type,
      allowed_tw_levels: updates.allowed_tw_levels,
      allowed_training_modules: updates.allowed_training_modules,
      allowed_ticket_types: updates.allowed_ticket_types,
      app_ids: updates.app_ids,
      knowledge_area_ids: updates.knowledge_area_ids,
      app_knowledge_areas: updates.app_knowledge_areas,
    }
    try {
      await apiRequest<Role>(`/supervisor/roles/${roleId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      })
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'updateSupervisorRole' })
      throw error
    }
  },

  async deleteSupervisorRole(roleId: string): Promise<void> {
    try {
      await apiRequest(`/supervisor/roles/${roleId}`, { method: 'DELETE' })
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'deleteSupervisorRole' })
      throw error
    }
  },

  async getSupervisorUserRoles(userId: string): Promise<Role[]> {
    try {
      const response = await apiRequest<{ user_id: string; roles: Role[] }>(`/supervisor/users/${userId}/roles`)
      return response?.roles ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getSupervisorUserRoles' })
      return []
    }
  },

  async assignSupervisorRoleToUser(userId: string, roleId: string): Promise<void> {
    await apiRequest(`/supervisor/users/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ role_id: roleId }),
    })
  },

  async removeSupervisorRoleFromUser(userId: string, roleId: string): Promise<void> {
    await apiRequest(`/supervisor/users/${userId}/roles/${roleId}`, { method: 'DELETE' })
  },

  // Read-only prefill sources for edit / create-from-role in supervisor mode
  // (tqs#742) — the /roles/{id}/... equivalents are admin-only.
  async getSupervisorRoleAppAssignments(roleId: string): Promise<string[]> {
    try {
      const response = await apiRequest<RoleAppAssignmentsResponse>(`/supervisor/roles/${roleId}/apps`)
      return response?.app_ids ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getSupervisorRoleAppAssignments', roleId })
      return []
    }
  },

  async getSupervisorRoleKnowledgeAreaAssignments(roleId: string): Promise<string[]> {
    try {
      const response = await apiRequest<RoleKnowledgeAreaAssignmentsResponse>(`/supervisor/roles/${roleId}/knowledge-areas`)
      return response?.knowledge_area_ids ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getSupervisorRoleKnowledgeAreaAssignments', roleId })
      return []
    }
  },

  async getSupervisorUsersWithRole(roleId: string): Promise<string[]> {
    try {
      return await roleManagementService.getRoleHolderIds(roleId, 'supervisor')
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getSupervisorUsersWithRole', roleId })
      return []
    }
  },

  // ============== Supervisor Knowledge Area Management ==============

  async getSupervisorKnowledgeAreas(): Promise<CdbTqsKnowledgeAreaResponse[]> {
    try {
      const response = await apiRequest<{ items: CdbTqsKnowledgeAreaResponse[]; total: number }>('/supervisor/knowledge-areas')
      return response?.items ?? []
    } catch (error) {
      logError(error as Error, { context: 'RoleManagementService', method: 'getSupervisorKnowledgeAreas' })
      return []
    }
  },

  async createSupervisorKnowledgeArea(data: CreateKnowledgeAreaPayload): Promise<CdbTqsKnowledgeAreaResponse | null> {
    return apiRequest<CdbTqsKnowledgeAreaResponse>('/supervisor/knowledge-areas', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async updateSupervisorKnowledgeArea(kaId: string, data: Partial<CdbTqsKnowledgeAreaBase>): Promise<CdbTqsKnowledgeAreaResponse | null> {
    return apiRequest<CdbTqsKnowledgeAreaResponse>(`/supervisor/knowledge-areas/${kaId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  async deleteSupervisorKnowledgeArea(kaId: string): Promise<void> {
    await apiRequest(`/supervisor/knowledge-areas/${kaId}`, { method: 'DELETE' })
  },
}
